import {useCallback, useMemo} from "react";
import {useCampaign} from "@/campaigns/CampaignContextHooks.ts";
import {ModalReturn, ModalTarget} from "@/modal/ModalHooks.ts";
import {useCampaignModalPresentation} from "@/modal/useCampaignModalPresentation.ts";
import {contentTarget, presentationContent, sameTarget} from "@/modal/ModalPresentationContent.ts";
import {useModalPreview} from "@/modal/useModalPreview.ts";

export function useModalController(): ModalReturn {
    const {campaign, isGameMaster} = useCampaign();
    const {
        value: presentationValue,
        loading: presentationLoading,
        updating: presentationUpdating,
        error: presentationError,
        update: updatePresentation,
    } = useCampaignModalPresentation(campaign.id);
    const {
        followsPresentation,
        target: previewTarget,
        content: previewContent,
        loading: previewLoading,
        error: previewError,
        show: showPreview,
        close: closePreview,
    } = useModalPreview(isGameMaster);
    const presentedContent = presentationContent(presentationValue);
    const presentedTarget = contentTarget(presentedContent);
    const content = followsPresentation ? presentedContent : previewContent;
    const target = followsPresentation ? presentedTarget : previewTarget;
    const loading = followsPresentation
        ? Boolean(presentedTarget) && presentationLoading
        : previewLoading;

    const show = useCallback((next: ModalTarget) => {
        showPreview(next);
        if (presentationValue.type !== "NONE") void updatePresentation({type: "NONE"});
    }, [presentationValue.type, showPreview, updatePresentation]);
    const showSpell = useCallback((id: string) => show({type: "SPELL", id}), [show]);
    const showCharacter = useCallback((id: string) => show({type: "CHARACTER", id}), [show]);

    const hide = useCallback(() => {
        closePreview();
        if (isGameMaster && presentationValue.type !== "NONE") {
            void updatePresentation({type: "NONE"});
        }
    }, [closePreview, isGameMaster, presentationValue.type, updatePresentation]);

    const setVisibleToPlayer = useCallback((visible: boolean) => {
        if (!isGameMaster || !target) return;
        if (visible) {
            void updatePresentation({type: target.type, contentId: target.id});
        } else {
            showPreview(target);
            void updatePresentation({type: "NONE"});
        }
    }, [isGameMaster, showPreview, target, updatePresentation]);

    const visibleToPlayer = sameTarget(target, presentedTarget);
    const error = (!followsPresentation ? previewError : undefined)
        ?? (presentationError && (target || presentedTarget)
            ? "Der Präsentationsstatus konnte nicht geladen oder geändert werden."
            : undefined);

    return useMemo(() => ({
        content,
        loading,
        error,
        updatingVisibility: presentationUpdating,
        visibleToPlayer,
        showSpell,
        showCharacter,
        hide,
        setVisibleToPlayer,
    }), [
        content,
        error,
        hide,
        loading,
        presentationUpdating,
        setVisibleToPlayer,
        showCharacter,
        showSpell,
        visibleToPlayer,
    ]);
}
